import React from 'react';
import { StandardTableActions, Badge, Select } from '../../components/ui';
import type { BadgeVariant } from '../../components/ui';

import { Column } from '../../components/DataTable/DataTable';
import { Reminder } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';

// Helper function to get reminder type badge variant
const getReminderTypeBadgeVariant = (type?: string): BadgeVariant => {
  if (!type) return 'secondary';
  const typeLower = type.toLowerCase();
  if (typeLower.includes('call')) return 'info';
  if (typeLower.includes('appointment')) return 'accent';
  if (typeLower.includes('offer')) return 'warning';
  if (typeLower.includes('invoice')) return 'success';
  if (typeLower.includes('payment')) return 'danger';
  if (typeLower.includes('task')) return 'accent';
  if (typeLower.includes('support') || typeLower.includes('ticket')) return 'warning';
  return 'secondary';
};

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Reminders',
    description: 'Manage your client reminders database',
    permissionModuleKey: 'reminders',
    route: {
      list: '/reminders',
      new: '/reminders/new',
      detail: (id: number) => `/reminders/${id}`,
      edit: (id: number) => `/reminders/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-clients-reminders',
      detail: (id: number) => `/crm-clients-reminders/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search reminders by client name...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'clientId', 'assignedTo', 'clientReminderType', 'clientReminderIsDone', 'clientReminderDateTime'
      ],
    },
    {
      name: 'details',
      label: 'Details',
      icon: '📋',
      fields: [
        'clientReminderDescr', 'clientReminderNotes'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'clientId',
        label: 'Client',
        placeholder: 'All Clients',
        type: 'select' as const,
        getValue: (item: Reminder) => 
          item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : ''),
        options: [],
      },
      {
        key: 'assignedTo',
        label: 'Assigned To',
        placeholder: 'All Users',
        type: 'select' as const,
        getValue: (item: Reminder) => 
          item.assignedToUser
            ? (item.assignedToUser.userDisplayName || item.assignedToUser.userFirstName || `User #${item.assignedToUser.id}`)
            : (item.assignedTo ? `User #${item.assignedTo}` : ''),
        options: [],
      },
      {
        key: 'clientReminderType',
        label: 'Type',
        placeholder: 'All Types',
        type: 'select' as const,
        getValue: (item: Reminder) => 
          item.clientReminderTypeDescr?.pr_descr || (item.clientReminderType ? `Type #${item.clientReminderType}` : ''),
        options: [],
      },
      {
        key: 'clientReminderIsDone',
        label: 'Is Done',
        placeholder: 'All Status',
        type: 'boolean' as const,
        getValue: (item: Reminder) => item.clientReminderIsDone ? 'Done' : 'Not Done',
        options: ['Done', 'Not Done'],
      },
      {
        key: 'clientReminderDateTime',
        label: 'Date & Time',
        placeholder: 'All Date & Time',
        type: 'date' as const,
        getValue: (item: Reminder) =>
          item.clientReminderDateTime
            ? formatDateGlobal(item.clientReminderDateTime)
            : undefined,
        options: [],
      },
      {
        key: 'clientReminderDescr',
        label: 'Description',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: Reminder) => item.clientReminderDescr,
        options: [],
      },
      {
        key: 'clientReminderNotes',
        label: 'Notes',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: Reminder) => item.clientReminderNotes,
        options: [],
      },
    ],
    applyFilter: (
      items: Reminder[],
      filters: {
        clientId: string[];
        assignedTo: string[];
        clientReminderType: string[];
        clientReminderIsDone: string[];
        clientReminderDateTime: string[];
        clientReminderDescr: string[] | string;
        clientReminderNotes: string[] | string;
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Client filter
        if (filters.clientId && filters.clientId.length > 0) {
          const displayValue = item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : '');
          const matches = filters.clientId.some((filter) => {
            return displayValue === filter || 
                   (item.clientId && filter === `Client #${item.clientId}`) ||
                   (item.client?.clientName && filter === item.client.clientName);
          });
          if (!matches) return false;
        }

        // Assigned To filter
        if (filters.assignedTo && filters.assignedTo.length > 0) {
          const displayValue = item.assignedToUser
            ? (item.assignedToUser.userDisplayName || item.assignedToUser.userFirstName || `User #${item.assignedToUser.id}`)
            : (item.assignedTo ? `User #${item.assignedTo}` : '');
          const matches = filters.assignedTo.some((filter) => {
            return displayValue === filter ||
                   (item.assignedTo && filter === `User #${item.assignedTo}`) ||
                   (item.assignedToUser?.userDisplayName && filter === item.assignedToUser.userDisplayName) ||
                   (item.assignedToUser?.id && filter === `User #${item.assignedToUser.id}`);
          });
          if (!matches) return false;
        }

        // Reminder Type filter
        if (filters.clientReminderType && filters.clientReminderType.length > 0) {
          const displayValue = item.clientReminderTypeDescr?.pr_descr || (item.clientReminderType ? `Type #${item.clientReminderType}` : '');
          const matches = filters.clientReminderType.some((filter) => {
            return displayValue === filter ||
                   (item.clientReminderType && filter === `Type #${item.clientReminderType}`) ||
                   (item.clientReminderTypeDescr?.pr_descr && filter === item.clientReminderTypeDescr.pr_descr);
          });
          if (!matches) return false;
        }

        // Is Done filter
        if (filters.clientReminderIsDone && filters.clientReminderIsDone.length > 0) {
          const status = item.clientReminderIsDone ? 'Done' : 'Not Done';
          if (!filters.clientReminderIsDone.includes(status)) return false;
        }

        // Date & Time filter
        if (filters.clientReminderDateTime.length > 0) {
          if (ranges.clientReminderDateTime) {
            const range = ranges.clientReminderDateTime;
            if (range.start && range.end && item.clientReminderDateTime) {
              const itemDate = new Date(item.clientReminderDateTime);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.clientReminderDateTime
              ? formatDateGlobal(item.clientReminderDateTime)
              : '';
            const matches = filters.clientReminderDateTime.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Description filter (text)
        const descrFilter = filters.clientReminderDescr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.clientReminderDescr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.clientReminderDescr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Notes filter (text)
        const notesFilter = filters.clientReminderNotes;
        if (typeof notesFilter === 'string' && notesFilter.trim() !== '') {
          const value = (item.clientReminderNotes || '').toLowerCase();
          if (!value.includes(notesFilter.toLowerCase())) return false;
        } else if (Array.isArray(notesFilter) && notesFilter.length > 0) {
          const value = (item.clientReminderNotes || '').toLowerCase();
          const matches = notesFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        return true;
      });
    },
  },

  // Toolbar Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-csv',
            label: 'Export to CSV',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToCSV } = await import('../../services/exportService');
                await exportToCSV(exportData);
              } catch (error: any) {
                console.error('Failed to export to CSV:', error);
                throw new Error(error?.message || 'Failed to export to CSV. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
          {
            key: 'export-pdf',
            label: 'Export to PDF',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }
              try {
                const { exportToPDF } = await import('../../utils/exportToPdf');
                await exportToPDF(exportData);
              } catch (error: any) {
                console.error('Failed to export to PDF:', error);
                throw new Error(error?.message || 'Failed to export to PDF. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Reminder',
        showTitle: false,
        variant: 'primary' as const,
        href: '/reminders/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Reminder) => {
      window.location.href = `/reminders/${item.clientReminderId}`;
    },
    keyExtractor: (item: Reminder) => item.clientReminderId,
    getClientId: (item: Reminder) => item.clientId ?? null,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new reminder.',
    noSearchResults: 'No reminders found matching your search criteria.',
    noFilterResults: 'No reminders match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Reminder',
    message: 'Are you sure you want to delete this reminder? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete reminder. Please try again.',
    handler: async (id: number) => {
      const { deleteReminder } = await import('../../services/mutations');
      await deleteReminder(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Card View Configuration
  cardView: {
    getId: (item: Reminder) => item.clientReminderId,
    getTitle: (item: Reminder) => {
      const truncated = item.clientReminderDescr 
        ? (item.clientReminderDescr.length > 50 ? item.clientReminderDescr.substring(0, 50) + '...' : item.clientReminderDescr)
        : `Reminder #${item.clientReminderId}`;
      return truncated;
    },
    getSubtitle: (item: Reminder) => {
      if (item.client?.clientName) {
        return `Client: ${item.client.clientName}`;
      }
      return '';
    },
    getStatusBadge: (item: Reminder) => ({
      label: item.clientReminderIsDone ? 'Done' : 'Pending',
      variant: (item.clientReminderIsDone ? 'success' : 'warning') as 'default' | 'success' | 'warning' | 'danger' | 'info',
    }),
    getCardFields: (item: Reminder) => {
      const fields: Array<{
        label?: string;
        value: React.ReactNode;
        icon?: React.ReactNode;
        badge?: boolean;
        badgeVariant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'blue' | 'purple';
      }> = [];

      // Client
      if (item.client?.clientName) {
        fields.push({
          value: item.client.clientName,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ),
        });
      }

      // Assigned To User
      if (item.assignedToUser) {
        const assignedName = item.assignedToUser.userDisplayName || item.assignedToUser.userFirstName || `User #${item.assignedToUser.id}`;
        fields.push({
          label: 'Assigned To',
          value: assignedName,
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ),
        });
      }

      // Reminder Type
      if (item.clientReminderTypeDescr?.pr_descr) {
        const type = item.clientReminderTypeDescr.pr_descr;
        const variant = getReminderTypeBadgeVariant(type);
        const badgeVariant =
          variant === 'info' ? 'blue' as const
          : variant === 'accent' ? 'purple' as const
          : variant === 'success' ? 'success' as const
          : variant === 'warning' ? 'warning' as const
          : variant === 'danger' ? 'danger' as const
          : 'default' as const;
        fields.push({
          value: type,
          badge: true,
          badgeVariant,
        });
      }

      // Date & Time
      if (item.clientReminderDateTime) {
        fields.push({
          label: 'Date & Time',
          value: formatDateGlobal(item.clientReminderDateTime),
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ),
        });
      }

      // Created Date
      if (item.clientReminderCreatedAt) {
        fields.push({
          label: 'Created',
          value: formatDateGlobal(item.clientReminderCreatedAt),
          icon: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        });
      }

      return fields;
    },
    statusFilter: {
      enabled: true,
      defaultFilter: 'all',
      options: [
        { value: 'all', label: 'All Reminders' },
        { value: 'pending', label: 'Pending' },
        { value: 'done', label: 'Done' },
      ],
      getStatus: (item: Reminder) => (item.clientReminderIsDone ? 'done' : 'pending'),
    },
    updateStatus: async (id: number, newStatus: any) => {
      const { updateReminder } = await import('../../services/mutations');
      await updateReminder(id, { clientReminderIsDone: newStatus === 'done' });
    },
    renderStatusSelector: (item: Reminder, isUpdating: boolean, onStatusChange: (newStatus: any) => void) => {
      return (
        <Select
          value={item.clientReminderIsDone ? 'done' : 'pending'}
          onChange={(e: any) => onStatusChange(e.target.value)}
          disabled={isUpdating}
          className="text-xs"
          showClearButton={false}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'done', label: 'Done' },
          ]}
        />
      );
    },
    actions: {
      view: true,
      edit: true,
      delete: true,
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Reminder Details',
    notFoundTitle: 'Reminder not found',
    notFoundMessage: 'The reminder you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Reminders',
    defaultTitle: 'Unnamed Reminder',
    getTitle: (item: Reminder) => {
      return item.clientReminderDescr || `Reminder #${item.clientReminderId}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Reminder',
      subtitle: 'Add a new reminder to your database',
      submitText: 'Create Reminder',
    },
    edit: {
      title: 'Edit Reminder',
      submitText: 'Update Reminder',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading reminder...',
    errorMessage: 'Failed to save reminder. Please try again.',
    create: async (data: Partial<Reminder>) => {
      const { createReminder } = await import('../../services/mutations');
      return await createReminder(data);
    },
    update: async (id: number, data: Partial<Reminder>) => {
      const { updateReminder } = await import('../../services/mutations');
      return await updateReminder(id, data);
    },
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      placeholder: 'Select a client',
      required: true,
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/crm-clients', // API endpoint to fetch all client options
      labelField: 'clientName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'assignedTo',
      label: 'Assigned To',
      type: 'select',
      required: true,
      placeholder: 'Select user',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/users', // API endpoint to fetch all user options
      labelField: 'userDisplayName', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'clientReminderType',
      label: 'Type',
      type: 'select',
      required: true,
      placeholder: 'Select reminder type',
      options: [], // Will be populated dynamically via API
      apiEndpoint: '/p-crm-reminder-type', // API endpoint to fetch all reminder type options
      labelField: 'pr_descr', // Field name in API response to use as label
      validation: {
        required: true,
      },
    },
    {
      name: 'clientReminderDateTime',
      label: 'Date & Time',
      type: 'datetime',
      placeholder: 'Select date and time',
      required: true,
      validation: {
        required: true,
      },
    },
    {
      name: 'clientReminderIsDone',
      label: 'Is Done',
      type: 'switch',
      switchLabel: 'Mark as done',
      description: 'Check if this reminder is completed',
      labelPosition: 'left',
      defaultValue: false,
    },
    {
      name: 'clientReminderDescr',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Enter reminder description',
      rows: 6,
      validation: {
        maxLength: 2000,
      },
    },
    {
      name: 'clientReminderNotes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter reminder notes',
      rows: 6,
      validation: {
        maxLength: 5000,
      },
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📋',
        gridCols: 'md:grid-cols-2',
        fields: ['clientId', 'assignedTo', 'clientReminderType', 'clientReminderDateTime'],
        collapsible: true,
      },
      {
        id: 'status',
        title: 'Status',
        icon: '✅',
        gridCols: 'md:grid-cols-2',
        fields: ['clientReminderIsDone'],
        collapsible: true,
      },
      {
        id: 'details',
        title: 'Reminder Details',
        icon: '📝',
        gridCols: 'md:grid-cols-12',
        fields: ['clientReminderDescr', 'clientReminderNotes'],
        fieldSpans: {
          clientReminderDescr: 'md:col-span-12',
          clientReminderNotes: 'md:col-span-12',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns
export const getColumns = (formatDate: (dateString?: string) => string): Column<Reminder>[] => [
  {
    key: 'clientReminderIsDone',
    header: 'Done',
    sortable: true,
    render: (value) => {
      if (value) {
        return (
          <div className="flex items-center justify-center">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      }
      return <span className="text-sm text-gray-400">-</span>;
    },
  },
  {
    key: 'client.clientName',
    header: 'Client',
    sortable: true,
    render: (value, row) => (
      row.client?.clientName || '-'
    ),
  },
  {
    key: 'assignedTo',
    header: 'Assigned To',
    sortable: true,
    render: (value, row) => {
      const u = row.assignedToUser;
      if (u) {
        const display =
          u.userDisplayName ||
          `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() ||
          (value ? `User #${value}` : '');
        return display || '-';
      }
      return value ? `User #${value}` : '-';
    },
  },
  {
    key: 'clientReminderDateTime',
    header: 'Date & Time',
    sortable: true,
    render: (value) => formatDate(value),
  },
  {
    key: 'clientReminderType',
    header: 'Type',
    sortable: true,
    render: (value, row) => {
      const type = row.clientReminderTypeDescr?.pr_descr;
      if (!type) return '-';
      
      const variant = getReminderTypeBadgeVariant(type);
      return (
        <Badge variant={variant}>
          {type}
        </Badge>
      );
    },
  },
  {
    key: 'clientReminderDescr',
    header: 'Description',
    sortable: true,
    render: (value) => {
      if (!value) return '-';
      return value.length > 100 ? value.substring(0, 100) + '...' : value;
    },
  },
  {
    key: 'clientReminderNotes',
    header: 'Notes',
    sortable: true,
    width: '200px', // Set column width
    render: (value) => {
      if (!value) return '-';
      return value.length > 50 ? value.substring(0, 50) + '...' : value;
    },
  },
  {
    key: 'clientReminderCreatedAt',
    header: 'Created At',
    sortable: true,
    render: (value) => formatDate(value),
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: Reminder,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📋',
      fields: [
        {
          label: 'Client',
          value: item.client?.clientName || (item.clientId ? `Client #${item.clientId}` : 'None'),
        },
        {
          label: 'Assigned To',
          value: (() => {
            const u = item.assignedToUser;
            if (u) {
              const display =
                u.userDisplayName ||
                `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() ||
                (item.assignedTo ? `User #${item.assignedTo}` : 'Unassigned');
              return display;
            }
            return item.assignedTo ? `User #${item.assignedTo}` : 'Unassigned';
          })(),
        },
        {
          label: 'Type',
          value: (() => {
            const type = item.clientReminderTypeDescr?.pr_descr;
            if (!type) return '-';
            const variant = getReminderTypeBadgeVariant(type);
            return (
              <Badge variant={variant}>
                {type}
              </Badge>
            );
          })(),
        },
        {
          label: 'Date & Time',
          value: formatDate(item.clientReminderDateTime),
        },
        {
          label: 'Is Done',
          value: item.clientReminderIsDone ? (
            <span className="inline-flex items-center gap-1 text-green-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Done
            </span>
          ) : (
            <span className="text-gray-500">Not Done</span>
          ),
        },
      ],
    },
    {
      title: 'Reminder Details',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.clientReminderDescr || '-',
        },
        {
          label: 'Notes',
          value: item.clientReminderNotes || '-',
        },
      ],
    },
    {
      title: 'System Information',
      icon: '⚙️',
      fields: [
        {
          label: 'Created At',
          value: formatDate(item.clientReminderCreatedAt),
        },
        {
          label: 'Created By',
          value: (() => {
            const u = item.clientReminderCreatedUser;
            if (u) {
              const display =
                u.userDisplayName ||
                `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() ||
                (item.clientReminderCreatedBy ? `User #${item.clientReminderCreatedBy}` : '-');
              return display;
            }
            return item.clientReminderCreatedBy ? `User #${item.clientReminderCreatedBy}` : '-';
          })(),
        },
        {
          label: 'Updated At',
          value: formatDate(item.clientReminderUpdatedAt),
        },
        {
          label: 'Updated By',
          value: (() => {
            const u = item.clientReminderUpdatedUser;
            if (u) {
              const display =
                u.userDisplayName ||
                `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() ||
                (item.clientReminderUpdatedBy ? `User #${item.clientReminderUpdatedBy}` : '-');
              return display;
            }
            return item.clientReminderUpdatedBy ? `User #${item.clientReminderUpdatedBy}` : '-';
          })(),
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: Reminder) => {
  if (item.clientReminderIsDone) {
    return (
      <Badge variant="success" className="gap-1">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        Done
      </Badge>
    );
  }
  return null;
};

// Helper function to get toolbar icons
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to get table actions
export const getTableActions = (
  item: Reminder,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.clientReminderId)}
      editTo={moduleConfig.module.route.edit(item.clientReminderId)}
      onDelete={() => onDelete(item.clientReminderId)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<Reminder> => {
  return {
    clientId: undefined,
    assignedTo: undefined,
    clientReminderType: undefined,
    clientReminderDateTime: undefined,
    clientReminderDescr: '',
    clientReminderNotes: '',
    clientReminderIsDone: false,
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: Reminder): Partial<Reminder> => {
  return {
    clientId: item.clientId,
    assignedTo: item.assignedTo,
    clientReminderType: item.clientReminderType,
    clientReminderDateTime: item.clientReminderDateTime,
    clientReminderDescr: item.clientReminderDescr || '',
    clientReminderNotes: item.clientReminderNotes || '',
    clientReminderIsDone: item.clientReminderIsDone ?? false,
  };
};

